import React, { useEffect, useState, useRef, useCallback } from "react";
import { utils } from "../../Helpers/utils";
import { SearchInput, Toast } from "../../Components";
import OfferItem from "./OfferItem";
import { offerService } from "../../Services";
import NotSubscribe from "../../Components/Base/Common/NotSubscribe";
import { storageHandler } from "../../Helpers/storage";
import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
} from "@mui/material";
import { Search, Loader2 } from "lucide-react";
import { useSidebar } from "../../Components/ui/sidebarNew";
import AppSelectInput from "../../Components/Base/Form/AppSelectInput";
import { parseInput } from "../../Components/Constants/AppConstant";

// Shimmer row component for loading state
const TableRowShimmer = ({ index }) => (
  <TableRow 
    sx={{ 
      '& td': { 
        py: 1.5, 
        px: 2.5,
        borderBottom: '1px solid #F3F4F6',
      },
      backgroundColor: index % 2 === 0 ? 'rgba(248, 250, 252, 0.5)' : 'transparent',
    }}
  >
    <TableCell sx={{ width: "40px", minWidth: "40px" }}>
      <div className="w-6 h-5 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "100px" }}>
      <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "150px" }}>
      <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "200px" }}>
      <div className="h-4 w-full rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "100px" }}>
      <div className="h-4 w-16 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "120px" }}>
      <div className="h-6 w-24 mx-auto rounded-full bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "150px" }}>
      <div className="h-8 w-20 rounded bg-gray-200 animate-pulse" />
    </TableCell>
  </TableRow>
);

const statusOptions = [
  { id: "all", value: "All Status" },
  { id: "p", value: "Pending" },
  { id: "c", value: "Complete" },
];

export default function Offer({
  setAuth,
  setModal,
  NotifModul,
  user,
  profiles,
  setModalNotification,
  closeCartPanel,
  cartItem,
  goToPricing,
  setOfferSelectedForReview,
  onStartChat,
}) {
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [offerDetails, setOfferDetails] = useState([]);
  const [toastList, setToastList] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [searchString, setSearchString] = useState("");
  const [searchList, setSearchList] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const { state } = useSidebar();
  const tableContainerRef = useRef(null);

  // Refs to track loading state for scroll handler
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);

  // Safety check for offerDetails
  const safeOfferDetails = Array.isArray(offerDetails) ? offerDetails : [];

  const selectedFilter =
    statusOptions.find((item) => item.id === selectedStatus.id) || statusOptions[0];

  const addToast = (data) => {
    const newToastList = [...toastList];
    newToastList.push({ ...data, id: `${Date.now()}-${toastList.length}` });
    setToastList(newToastList);
  };

  // Function to trigger data refresh
  const refreshOfferData = useCallback(() => {
    setPage(1);
    setOfferDetails([]);
    setHasMore(true);
    setSearchList((prev) => !prev);
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        // Show loading for first page, show fetching indicator for subsequent pages
        if (page === 1) {
          setIsLoaded(false);
        } else {
          setIsLoadingMore(true);
        }
        setIsFetching(true);

        let filter = "";
        const filters = [];

        if (selectedStatus.id && selectedStatus.id !== "all") {
          filters.push(`status=${selectedStatus.id}`);
        }

        if (searchString.trim()) {
          filters.push(`inf=${encodeURIComponent(searchString.trim())}`);
        }

        if (filters.length > 0) {
          filter = filters.join("&");
        }

        console.log("🔍 Fetching offers - page:", page, "filter:", filter);

        const resApi = await offerService.getAll({
          page: page,
          pageSize: pageSize,
          filter: filter,
        });

        const res = resApi.data;
        console.log("🚀 ~ fetchData ~ res:", res);

        if (res?.data) {
          const totalPages =
            res.last_page || Math.ceil((res.total || 0) / pageSize);
          const currentPage = res.current_page || page;

          console.log(
            "📊 fetchData result - page:",
            page,
            "currentPage:",
            currentPage,
            "totalPages:",
            totalPages,
            "hasMore:",
            currentPage < totalPages
          );

          setPaginationData({
            per_page: res.per_page || pageSize,
            total: res.total || 0,
            current_page: currentPage,
            total_pages: totalPages,
          });

          // For infinite scroll: append data on subsequent pages, replace on first page
          const newData = res.data || [];
          console.log("📊 newData length:", newData.length, "page:", page);

          if (page === 1) {
            setOfferDetails(newData);
          } else {
            setOfferDetails((prev) => {
              // Prevent duplicates
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewData = newData.filter(
                (item) => !existingIds.has(item.id)
              );
              console.log(`➕ Adding ${uniqueNewData.length} unique items`);
              return [...prev, ...uniqueNewData];
            });
          }

          // Check if there are more pages
          setHasMore(currentPage < totalPages);
        } else {
          setOfferDetails([]);
          setPaginationData({
            per_page: pageSize,
            total: 0,
            current_page: 1,
            total_pages: 0,
          });
          setHasMore(false);
        }

        setIsLoaded(true);
        setIsFetching(false);
        setIsLoadingMore(false);
      } catch (err) {
        console.error("❌ Error fetching offers:", err);

        if (err.apiResponseStatus !== 404) {
          let error = parseInput(err?.message);
          addToast({
            error: true,
            msg: error?.message || "Failed to load offers",
            id: `${Date.now()}-${toastList.length}`,
          });
        } else {
          setIsLoaded(true);
        }

        setIsFetching(false);
        setIsLoadingMore(false);
        setHasMore(false);
      }
    }

    fetchData();
  }, [page, selectedStatus.id, searchList]);

  // Load more data for infinite scroll
  const loadMoreData = useCallback(() => {
    // Use refs to get latest state values
    if (
      !isLoadingMoreRef.current &&
      hasMoreRef.current &&
      !isFetchingRef.current
    ) {
      console.log(
        "✅ loadMoreData called - incrementing page from",
        page,
        "to",
        page + 1
      );
      setPage((prev) => prev + 1);
    } else {
      console.log(
        "🚫 loadMoreData skipped - isLoadingMore:",
        isLoadingMoreRef.current,
        "hasMore:",
        hasMoreRef.current,
        "isFetching:",
        isFetchingRef.current
      );
    }
  }, [page]);

  // Scroll handler for infinite scroll
  const handleScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) {
      console.log("🔴 No container ref");
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    console.log(
      "📜 Scroll detected - scrollTop:",
      scrollTop,
      "clientHeight:",
      clientHeight,
      "scrollHeight:",
      scrollHeight,
      "nearBottom:",
      scrollTop + clientHeight >= scrollHeight - 150
    );

    // Use refs to get latest loading state
    if (isLoadingMoreRef.current || isFetchingRef.current) {
      console.log("⏳ Still loading, skipping...");
      return;
    }

    if (!hasMoreRef.current) {
      console.log("🛑 No more data to load");
      return;
    }

    // Load more when user scrolls near the bottom (within 150px)
    if (scrollTop + clientHeight >= scrollHeight - 150) {
      console.log("✅ Triggering loadMore!");
      loadMoreData();
    }
  }, [loadMoreData]);

  const handleStatusChange = (item) => {
    console.log("🎯 Status changed to:", item.value);
    setSelectedStatus(item);
    setPage(1);
    setOfferDetails([]);
    setHasMore(true);
    setSearchString("");
    setSearchList((prev) => !prev);
  };

  const setSearchStringText = (event) => {
    setSearchString(event.target.value);
  };

  const search = () => {
    console.log("🔍 Search triggered with:", searchString);
    setOfferDetails([]);
    setHasMore(true);
    setSearchList((prev) => !prev);
    setPage(1);
  };

  let data = null;
  try {
    const userInfo = storageHandler.getUserInfo();
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      data = parsed?.data || null;
    }
  } catch (error) {
    console.error("Error parsing user info:", error);
  }

  if (!data?.p_f_ids?.includes(12)) {
    return (
      <NotSubscribe
        goToPricing={goToPricing}
        type="brand"
        setAuth={setAuth}
        setModal={setModal}
        NotifModul={NotifModul}
        setNotifModul={setModalNotification}
        profiles={profiles}
        closeCartPanel={closeCartPanel}
        user={user}
        cartItem={cartItem}
        title={"Offers"}
        background={"background_offers.png"}
      />
    );
  }

  return (
    <>
      <div className="main">
        <div
          style={{
            marginLeft: state === "collapsed" ? "28px" : "",
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 100px)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* Header Section */}
            <header
              className="flex pb-4 flex-col md:flex-row md:items-center md:justify-between gap-3"
              style={{ flexShrink: 0, zIndex: 20, position: "relative" }}
            >
              <h1 className="text-2xl font-bold text-gray-800"></h1>

              {/* Actions Section */}
              <div className="flex flex-wrap items-center gap-5 justify-center">
                {/* Filter Select */}
                <div
                  style={{
                    width: "150px",
                    boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                    borderRadius: "9999px",
                  }}
                >
                  <AppSelectInput
                    list={statusOptions}
                    selected={selectedFilter}
                    onChange={handleStatusChange}
                    className="!rounded-full !border-none !bg-gray-50"
                    style={{ paddingBottom: "0px" }}
                  />
                </div>

                {/* Search Input */}
                <div
                  style={{
                    width: "280px",
                    boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                    borderRadius: "9999px",
                  }}
                >
                  <SearchInput
                    value={searchString}
                    onClick={search}
                    type="text"
                    icon={
                      <Search color="#9CA3AF" strokeWidth={2.2} size={18} />
                    }
                    placeholder="Search by influencer..."
                    onChange={setSearchStringText}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        search();
                      }
                    }}
                    className="!rounded-full !border-none !bg-gray-50 pb-0"
                    style={{ paddingBottom: "0px" }}
                  />
                </div>
              </div>
            </header>

            {/* Table Container */}
            <div
              className="rounded-xl bg-white overflow-hidden w-full"
              style={{
                boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                border: "1px solid rgba(16, 185, 129, 0.1)",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              <div
                ref={tableContainerRef}
                onScroll={handleScroll}
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#e5e7eb transparent",
                }}
                className="offers-table-scroll"
              >
                <style>
                  {`
                    @media (min-width: 1024px) {
                      .offers-table-scroll {
                        overflow-x: hidden !important;
                      }
                    }
                    .offers-table-scroll::-webkit-scrollbar {
                      width: 8px;
                    }
                    .offers-table-scroll::-webkit-scrollbar-track {
                      background: transparent;
                    }
                    .offers-table-scroll::-webkit-scrollbar-thumb {
                      background: #e5e7eb;
                      border-radius: 4px;
                    }
                    .offers-table-scroll::-webkit-scrollbar-thumb:hover {
                      background: #d1d5db;
                    }
                  `}
                </style>
                <Table
                  sx={{
                    width: "100%",
                    minWidth: { xs: 900, lg: "100%" },
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    tableLayout: { xs: "fixed", lg: "auto" },
                  }}
                >
                  <TableHead
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      background:
                        "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                      "& th": {
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        borderBottom: "2px solid #E2E8F0",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          width: { xs: "40px", lg: "40px" },
                          minWidth: "40px",
                        }}
                      />
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          width: { xs: "120px", lg: "auto" },
                          minWidth: "100px",
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          width: { xs: "180px", lg: "auto" },
                          minWidth: "150px",
                        }}
                      >
                        Influencer
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          width: { xs: "250px", lg: "auto" },
                          minWidth: "200px",
                        }}
                      >
                        Description
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          textAlign: "center",
                          width: { xs: "120px", lg: "auto" },
                          minWidth: "100px",
                        }}
                      >
                        Offered Amt
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          textAlign: "center",
                          width: { xs: "140px", lg: "auto" },
                          minWidth: "120px",
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          width: { xs: "180px", lg: "auto" },
                          minWidth: "150px",
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!isLoaded && safeOfferDetails.length === 0 ? (
                      // Show shimmer rows during initial loading
                      [...Array(8)].map((_, index) => (
                        <TableRowShimmer key={`shimmer-${index}`} index={index} />
                      ))
                    ) : safeOfferDetails.length > 0 ? (
                      safeOfferDetails.map((item, index) => (
                        <OfferItem
                          key={`${item.id}-${index}`}
                          user={user}
                          reFetchData={refreshOfferData}
                          onStartChat={onStartChat}
                          setOfferSelectedForReview={setOfferSelectedForReview}
                          {...item}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align="center"
                          sx={{
                            py: 6,
                            color: "#6B7280",
                            fontSize: "14px",
                          }}
                        >
                          No offers found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Loading indicator for infinite scroll */}
                {isLoadingMore && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "16px",
                      color: "#6B7280",
                      fontSize: "14px",
                      gap: "8px",
                    }}
                  >
                    <Loader2
                      size={20}
                      className="animate-spin"
                      style={{ color: "#10B981" }}
                    />
                    <span>Loading more...</span>
                  </div>
                )}

                {/* End of data indicator */}
                {!hasMore && safeOfferDetails.length > 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      color: "#9CA3AF",
                      fontSize: "13px",
                    }}
                  >
                    No more offers to load
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {toastList && <Toast list={toastList} setList={setToastList} />}
    </>
  );
}